import React, { useState, useEffect } from 'react';
import { adminApi } from './api';
import { useDarkMode } from '../hooks/useDarkMode';
import Overview from './Overview';
import AiCallLogs from './AiCallLogs';
import GenerationRecords from './GenerationRecords';
import UserManagement from './UserManagement';
import FlowModelManagement from './FlowModelManagement';
import TransferStyleManagement from './TransferStyleManagement';
import AdminFloatingAssistant from './AdminFloatingAssistant';

type Section = 'overview' | 'ai-call-logs' | 'generation-records' | 'users' | 'flow-models' | 'transfer-styles';

const NAV_ITEMS: { key: Section; label: string }[] = [
  { key: 'overview', label: '📊 概覽' },
  { key: 'ai-call-logs', label: '🤖 AI 呼叫記錄' },
  { key: 'generation-records', label: '📝 生成記錄' },
  { key: 'users', label: '👥 使用者管理' },
  { key: 'flow-models', label: '📋 工具包管理' },
  { key: 'transfer-styles', label: '🎨 風格轉換管理' },
];

// ── Login Form ──────────────────────────────────────────────────────────────

const LoginForm: React.FC<{ onLogin: (username: string) => void }> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const data = await adminApi.login(username, password);
      onLogin(data.username);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-950">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-md w-80">
        <h2 className="text-xl font-bold mb-1 text-gray-800 dark:text-gray-100">Jodoni 後台</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">管理員登入</p>
        {error && (
          <p className="text-red-500 text-sm mb-4 bg-red-50 dark:bg-red-900/30 px-3 py-2 rounded">{error}</p>
        )}
        <div className="mb-4">
          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">帳號</label>
          <input
            type="text" value={username} onChange={e => setUsername(e.target.value)}
            className="w-full border dark:border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            required autoFocus
          />
        </div>
        <div className="mb-6">
          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">密碼</label>
          <input
            type="password" value={password} onChange={e => setPassword(e.target.value)}
            className="w-full border dark:border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            required
          />
        </div>
        <button
          type="submit" disabled={submitting}
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
        >
          {submitting ? '登入中...' : '登入'}
        </button>
      </form>
    </div>
  );
};

// ── Admin Shell ─────────────────────────────────────────────────────────────

const AdminApp: React.FC = () => {
  const { isDark, toggle } = useDarkMode();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [section, setSection] = useState<Section>('overview');
  const [sectionData, setSectionData] = useState<Record<string, any>>({});

  useEffect(() => {
    adminApi.me()
      .then(data => { setUsername(data.username); setIsLoggedIn(true); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // 每 30 分鐘向後端確認 session 是否仍有效（後端 JWT 時效為 8 小時）。
  // 若 token 已過期，後端回傳 401，前端自動登出並導回登入頁。
  useEffect(() => {
    if (!isLoggedIn) return;
    const interval = setInterval(() => {
      adminApi.me().catch(() => {
        setIsLoggedIn(false);
        setUsername('');
      });
    }, 30 * 60 * 1000); // 每 30 分鐘檢查一次
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  // 登出前確認，防止誤觸
  const handleLogout = async () => {
    if (!confirm('確定要登出後台嗎？')) return;
    await adminApi.logout();
    setIsLoggedIn(false);
    setUsername('');
  };

  const handleSectionChange = (s: Section) => {
    setSection(s);
    setSectionData({});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-400 dark:bg-gray-950">
        載入中...
      </div>
    );
  }

  if (!isLoggedIn) {
    return <LoginForm onLogin={u => { setIsLoggedIn(true); setUsername(u); }} />;
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-950 font-sans">
      {/* Sidebar */}
      <aside className="w-52 bg-gray-900 text-white flex flex-col shrink-0">
        <div className="p-4 border-b border-gray-700">
          <h1 className="text-base font-bold">Jodoni 後台</h1>
          <p className="text-xs text-gray-400 mt-0.5 truncate">{username}</p>
        </div>
        <nav className="flex-1 p-2 space-y-0.5">
          {NAV_ITEMS.map(item => (
            <button
              key={item.key}
              onClick={() => handleSectionChange(item.key)}
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                section === item.key
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-700 space-y-3">
          {/* Dark mode toggle */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">{isDark ? '🌙 深色模式' : '☀️ 亮色模式'}</span>
            <button
              onClick={toggle}
              role="switch"
              aria-checked={isDark}
              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                isDark ? 'bg-blue-600' : 'bg-gray-500'
              }`}
            >
              <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
                isDark ? 'translate-x-5' : 'translate-x-1'
              }`} />
            </button>
          </div>
          <button onClick={handleLogout} className="text-xs text-gray-400 hover:text-white transition-colors">
            登出
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto p-6">
        {section === 'overview' && <Overview />}
        {section === 'ai-call-logs' && <AiCallLogs />}
        {section === 'generation-records' && <GenerationRecords />}
        {section === 'users' && <UserManagement />}
        {section === 'flow-models' && <FlowModelManagement onSectionDataChange={setSectionData} />}
        {section === 'transfer-styles' && <TransferStyleManagement />}
      </main>

      <AdminFloatingAssistant activeSection={section} onNavigate={handleSectionChange} sectionData={sectionData} />
    </div>
  );
};

export default AdminApp;
